import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supportApi } from '@/lib/api';
import { captureSupportContext, SUPPORT_CATEGORY_LABELS, type SupportCategory } from '@/lib/support-context';

interface SupportDialogProps {
  category: SupportCategory | null;
  onClose: () => void;
}

export function SupportDialog({ category, onClose }: SupportDialogProps) {
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (category) {
      setDescription('');
      setError('');
    }
  }, [category]);

  if (!category) return null;
  const labels = SUPPORT_CATEGORY_LABELS[category];

  const submit = async () => {
    const text = description.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      const { context, sentryEventId } = captureSupportContext(category);
      await supportApi.createTicket({ category, description: text, context, sentryEventId });
      toast.success('Recebemos seu relato, entraremos em contato por e-mail se necessário.');
      onClose();
    } catch {
      // mantém o texto digitado para nova tentativa
      setError('Não foi possível enviar agora. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !sending && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.hint}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={6}
          aria-label="Descrição"
          disabled={sending}
        />
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancelar</Button>
          <Button onClick={submit} disabled={sending || !description.trim()}>
            {sending ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
